import React from 'react'

// Components
import RowGroup from '../RowGroup'
import Panel from '../Panel'

// Local Components
import { CurrentPlan } from '../../components/CurrentPlan'
import ProjectedTotal from '../ProjectedTotal/ProjectedTotal'
import BillingCycle from '../../components/BillingCycle'

// GQL
import { BillingPlanFragment, BillingSeatsInfoFragment } from 'gqlTypes'

// Utils

// Styles
import {
  CanceledPill,
  BlockText,
  StyledPanelTitle,
} from './PlanOverviewPanel.styles'
import BillingFor from '../BillingFor'

interface PlanOverviewPanelProps {
  workspaceId: string
  subscriptionEnd?: string
  currentPlan?: BillingPlanFragment
  seats: BillingSeatsInfoFragment
  nextBillingCycleDate?: string
  projectedCost?: number
  isWorkspaceSubscriptionActive: boolean
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  trialEndDate: string

  onReactivateSubscription: () => void
  onCancelSeatScheduleChange: () => void
  onSeatScheduleChange: () => void
}

/**
 * PlanOverviewPanel
 *
 * This component holds all the data logic needed to fulfill {@link PlanOverviewPanel}.
 */
export const PlanOverviewPanel: React.FC<PlanOverviewPanelProps> = props => {
  const {
    workspaceId,
    isWorkspaceSubscriptionActive,
    isWorkspaceOnTrial,
    customerHasPaymentDetails,
    currentPlan,
    subscriptionEnd,
    seats,
    nextBillingCycleDate,
    projectedCost,
    trialEndDate,
    onReactivateSubscription,
    onCancelSeatScheduleChange,
    onSeatScheduleChange,
  } = props

  const isSubscriptionEnd = !!subscriptionEnd

  if (!isWorkspaceSubscriptionActive) {
    return (
      <Panel>
        <StyledPanelTitle>Plan Overview</StyledPanelTitle>
        <Panel.Body>
          <RowGroup.Row>
            <RowGroup.HeadingCell width={['70%', 1 / 4]}>
              Current Plan
            </RowGroup.HeadingCell>
            <RowGroup.CaptionCell width={['70%', 1 / 4]}>
              <div>
                <CanceledPill data-testid="canceled-subscription-badge">
                  Subscription Canceled
                </CanceledPill>
                <BlockText>
                  Your Workspace subscription has been canceled and documents
                  are now in read-only mode.
                </BlockText>
              </div>
            </RowGroup.CaptionCell>
          </RowGroup.Row>
        </Panel.Body>
      </Panel>
    )
  }

  return (
    <Panel>
      <StyledPanelTitle>Plan Overview</StyledPanelTitle>
      <Panel.Body>
        <RowGroup>
          <CurrentPlan
            workspaceId={workspaceId}
            isWorkspaceOnTrial={isWorkspaceOnTrial}
            customerHasPaymentDetails={customerHasPaymentDetails}
            currentPlan={currentPlan}
            trialEndDate={trialEndDate}
            isSubscriptionEnd={isSubscriptionEnd}
            onReactivateSubscription={onReactivateSubscription}
          />
          <BillingFor
            seats={seats}
            onSeatScheduleChange={onSeatScheduleChange}
          />
          <ProjectedTotal
            projectedCost={projectedCost}
            seats={seats}
            hasSubscription={customerHasPaymentDetails}
          />
          <BillingCycle
            nextBillingCycleDate={nextBillingCycleDate}
            subscriptionEnd={subscriptionEnd}
            seats={seats}
            isWorkspaceOnTrial={isWorkspaceOnTrial}
            customerHasPaymentDetails={customerHasPaymentDetails}
            onCancelSeatScheduleChange={onCancelSeatScheduleChange}
            currentPlan={currentPlan}
          />
        </RowGroup>
      </Panel.Body>
    </Panel>
  )
}
